import { Observable } from 'rxjs';
import { last, map } from 'rxjs/operators';

import {
    ValidationContext,
    ValidationContextBase,
    ValidatorFunctionContext,
} from './validation-context';
import { ValidatorMetadata } from './validation-metadata';
import { Validator } from './validator';
import { ValidationError } from './validation-error';

export abstract class ValidatorBase {
    metadata: ValidatorMetadata;

    protected constructor(
        readonly name: string,
        readonly group: string,
        readonly context?: Partial<ValidationContextBase>
    ) {}

    protected getKey(context: ValidationContext): string {
        let key = this.name;
        if (this.group) {
            key = `${key}_${this.group}`;
        }
        if (context.propertyName) {
            key = `${key}_${context.propertyName}`;
        }

        return key;
    }

    protected getValidatorFunctionContext(
        value: any,
        context?: ValidationContext
    ): ValidatorFunctionContext {
        return {
            ...(this.context || ({} as ValidationContextBase)),
            ...(context || ({} as ValidationContext)),
            displayName:
                (context && context.displayName) ||
                (this.context && this.context.displayName) ||
                (context && context.propertyName),
            value: value,
            validatorName: this.name,
        };
    }
}

export type ValidatorFunction = (
    value: any,
    context: ValidatorFunctionContext
) => boolean;
export type AsyncValidatorFunction = (
    value: any,
    context: ValidatorFunctionContext
) => Observable<boolean>;

export class SyncValidator extends ValidatorBase implements Validator {
    readonly isAsync = false;

    constructor(
        name: string,
        group: string,
        private validatorFn: ValidatorFunction,
        context?: Partial<ValidationContextBase>
    ) {
        super(name, group, context);
    }

    /** Run this validator against the specified value.
     *
        @param value {Object} Value to validate
        @param additionalContext {ValidationContext} Any additional contextual information that the Validator can make use of.
        @return One or more ValidationErrors if validation fails, null otherwise
     */
    validate(
        value: any,
        context?: ValidationContext
    ): ValidationError | ValidationError[] | null {
        const ctx = this.getValidatorFunctionContext(value, context);

        if (!this.validatorFn(value, ctx)) {
            return new ValidationError(this.getKey(ctx), ctx, this);
        }

        return null;
    }
}

export class AsyncValidator extends ValidatorBase implements Validator {
    readonly isAsync = true;

    constructor(
        name: string,
        group: string,
        private validatorFn: AsyncValidatorFunction,
        context?: Partial<ValidationContextBase>
    ) {
        super(name, group, context);
    }

    /** Run this validator against the specified value.
     *
        @param value {Object} Value to validate
        @param additionalContext {ValidationContext} Any additional contextual information that the Validator can make use of.
        @return One or more Observables completing to a validation error or null
     */
    validate(
        value: any,
        context?: ValidationContext
    ):
        | Observable<ValidationError | null>
        | Observable<ValidationError | null>[] {
        const ctx = this.getValidatorFunctionContext(value, context);

        return this.validatorFn(value, ctx).pipe(
            last(),
            map((isValid) => {
                if (isValid) {
                    return null;
                }

                return new ValidationError(this.getKey(ctx), ctx, this);
            })
        );
    }
}

export interface ValidatorFactory {
    name: string;
    fn: (group: string, context?: Partial<ValidationContextBase>) => Validator;
}
